import React from "react";

import { Alert, Skeleton } from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";

import { useAlert } from "../../contexts/AlertContext";
import ConfirmationButton from "../shared/ConfirmationButton";
import useLazyLoad, { LazyState } from "../shared/LazyLoad";

export type QueueInfo = {
  messageCount: string;
  queueLink: string;
};

type QueueCardProps = {
  title: string;
  getQueueInfo: () => Promise<QueueInfo>;
  purgeAction: () => void;
};

const QueueCard: React.FC<QueueCardProps> = ({ title, getQueueInfo, purgeAction }) => {
  const { alert } = useAlert();
  const { state, data: queueInfo, error } = useLazyLoad(getQueueInfo);

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(queueInfo?.queueLink!).then(
      () => {
        alert.success("Link copied to clipboard!");
      },
      (err) => {
        alert.error("Failed to copy to clipboard: " + err);
      },
    );
  };

  const renderContent = () => {
    switch (state) {
      case LazyState.Loaded:
        return (
          <>
            <Typography variant="body2" color="text.secondary" paragraph={true}>
              Messages count: {queueInfo?.messageCount}
            </Typography>
            <Typography variant="body1">{queueInfo?.queueLink}</Typography>
          </>
        );
      case LazyState.Error:
        return <Alert severity="error">{error?.message || "Unknown error"}</Alert>;
      default:
        return (
          <Typography variant="body1">
            <Skeleton />
          </Typography>
        );
    }
  };

  return (
    <Card role="group" aria-busy={state === LazyState.Loading} aria-label={title}>
      <CardContent sx={{ minHeight: "120px", lineBreak: "anywhere" }}>
        <Typography variant="h6" role="heading">
          {title}
        </Typography>
        {renderContent()}
      </CardContent>
      {state === LazyState.Loaded && (
        <CardActions>
          <ConfirmationButton
            onConfirm={purgeAction}
            variant="outlined"
            dialogTitle="Confirm Purge"
            dialogMessage={`Are you sure you want to purge ${title.toLowerCase()}?`}
            size="small"
          >
            Purge
          </ConfirmationButton>
          <Button size="small" variant="outlined" onClick={copyLinkToClipboard}>
            Copy Link
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

export default QueueCard;
